import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBolt,
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { defaultButtonStyle, lessPriorityButtonStyle } from "../../../styles/buttonStyles";
import RichTextEditor from "../../common/RTEEditor";
import { EmailHighlight, parseEmailHighlights } from "../../../utils/emailHighlights";
import "./ContactComposeEmailPopup.css";

interface ContactComposeEmailPopupProps {
  isOpen: boolean;
  onClose: () => void;
  blueprints: Array<{ id: number; templateName: string; templateDefinitionName?: string | null }>;
  fromOptions: Array<{
    id?: number;
    outboxId?: number;
    OutboxId?: number;
    username?: string;
    emailAddress?: string;
    fromEmail?: string;
    email?: string;
    type?: string;
    smtpType?: string;
  }>;
  selectedFromId: string;
  onFromChange: (value: string) => void;
  toEmail: string;
  signatureHtml: string;
  isSignatureLoading?: boolean;
  /** `manualInstruction` is the one-off steer typed in the editor's
   *  instruction column; it fills {manual_instruction} in the prompt. */
  onGenerate: (blueprintId: number, manualInstruction?: string) => Promise<{
    emailBody: string;
    emailSubject: string;
    finalPrompt?: string;
    webSearchData?: string;
    emails?: string;
    notes?: string;
    professionalSummary?: string;
    /** Highlight records as the generator returned them, raw or JSON. */
    emailHighlights?: unknown;
  }>;
  onSend: (payload: {
    emailSubject: string;
    emailBody: string;
    ccEmails: string[] | null;
    bccEmails: string[] | null;
  }) => Promise<boolean>;
  isSending: boolean;
}

const inputStyle: React.CSSProperties = {
  height: 42,
  border: "1px solid #d8dee8",
  borderRadius: 6,
  padding: "0 14px",
  fontSize: 13,
  color: "#111827",
  background: "#fff",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  width: 120,
  fontSize: 13,
  fontWeight: 700,
  color: "#111827",
};

const compactFieldStyle: React.CSSProperties = {
  width: 390,
  minWidth: 390,
  maxWidth: 390,
  flex: "0 0 390px",
};

export const parseRecipientInput = (value: string) =>
  value
    .split(/[,\n;]/)
    .map((email) => email.trim())
    .filter(Boolean);

export const mergeRecipients = (current: string[], next: string[]) => {
  const seen = new Set(current.map((email) => email.toLowerCase()));
  const merged = [...current];
  next.forEach((email) => {
    if (!seen.has(email.toLowerCase())) {
      seen.add(email.toLowerCase());
      merged.push(email);
    }
  });
  return merged;
};

interface RecipientChipInputProps {
  recipients: string[];
  draft: string;
  onRecipientsChange: (recipients: string[]) => void;
  onDraftChange: (draft: string) => void;
  placeholder: string;
  containerStyle?: React.CSSProperties;
  /** Static label rendered inside the panel (e.g. "To") so a filled field stays identifiable. */
  prefixLabel?: string;
  /** Renders the addresses as a completed panel — nothing to type or remove. */
  readOnly?: boolean;
  /** Caps how many addresses can be entered (Forward accepts a single recipient). */
  maxRecipients?: number;
}

export const RecipientChipInput: React.FC<RecipientChipInputProps> = ({
  recipients,
  draft,
  onRecipientsChange,
  onDraftChange,
  placeholder,
  containerStyle,
  prefixLabel,
  readOnly = false,
  maxRecipients,
}) => {
  const applyLimit = (nextRecipients: string[]) =>
    maxRecipients === undefined ? nextRecipients : nextRecipients.slice(0, maxRecipients);
  const canAddMore = maxRecipients === undefined || recipients.length < maxRecipients;

  const commitDraft = () => {
    const nextRecipients = parseRecipientInput(draft);
    if (!nextRecipients.length) {
      onDraftChange("");
      return;
    }
    onRecipientsChange(applyLimit(mergeRecipients(recipients, nextRecipients)));
    onDraftChange("");
  };

  const removeRecipient = (recipientToRemove: string) => {
    onRecipientsChange(recipients.filter((recipient) => recipient !== recipientToRemove));
  };

  return (
    <div
      className="recipient-chip-input"
      style={{
        ...compactFieldStyle,
        minHeight: 42,
        border: "1px solid #d8dee8",
        borderRadius: 6,
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        background: "#fff",
        cursor: readOnly ? "default" : "text",
        ...containerStyle,
      }}
      onClick={(event) => {
        if (readOnly) return;
        const input = event.currentTarget.querySelector("input");
        input?.focus();
      }}
    >
      {prefixLabel && (
        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600, lineHeight: 1 }}>
          {prefixLabel}
        </span>
      )}
      {recipients.map((recipient) => (
        <span
          key={recipient}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            maxWidth: "100%",
            border: "1px solid #c7d0dc",
            borderRadius: 999,
            padding: "5px 9px",
            background: "#f8fafc",
            color: "#0f172a",
            fontSize: 13,
            lineHeight: 1,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {recipient}
          </span>
          {!readOnly && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeRecipient(recipient);
              }}
              style={{
                border: "none",
                background: "transparent",
                color: "#334155",
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
                display: "inline-flex",
              }}
              aria-label={`Remove ${recipient}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </span>
      ))}
      {!readOnly && canAddMore && (
        <input
          value={draft}
          onChange={(event) => {
            const value = event.target.value;
            if (/[,\n;]/.test(value)) {
              onRecipientsChange(applyLimit(mergeRecipients(recipients, parseRecipientInput(value))));
              onDraftChange("");
              return;
            }
            onDraftChange(value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === "Tab") {
              if (draft.trim()) {
                event.preventDefault();
                commitDraft();
              }
            } else if (event.key === "Backspace" && !draft && recipients.length) {
              onRecipientsChange(recipients.slice(0, -1));
            }
          }}
          onBlur={commitDraft}
          style={{
            flex: "1 1 120px",
            minWidth: 90,
            border: "none",
            outline: "none",
            height: 28,
            fontSize: 13,
            color: "#111827",
          }}
          placeholder={recipients.length ? "" : placeholder}
        />
      )}
    </div>
  );
};

const ContactComposeEmailPopup: React.FC<ContactComposeEmailPopupProps> = ({
  isOpen,
  onClose,
  blueprints,
  fromOptions,
  selectedFromId,
  onFromChange,
  toEmail,
  signatureHtml,
  isSignatureLoading = false,
  onGenerate,
  onSend,
  isSending,
}) => {
  const [selectedBlueprintId, setSelectedBlueprintId] = useState("");
  const blueprintGroups = React.useMemo(() => {
    const groups = new Map<string, ContactComposeEmailPopupProps["blueprints"]>();
    blueprints.forEach((blueprint) => {
      const category = (blueprint.templateDefinitionName || "").trim() || "Uncategorized";
      const group = groups.get(category) || [];
      group.push(blueprint);
      groups.set(category, group);
    });
    return Array.from(groups, ([category, items]) => ({
      category,
      blueprints: items.sort((a, b) =>
        a.templateName.toLowerCase().localeCompare(b.templateName.toLowerCase()),
      ),
    })).sort((a, b) => a.category.localeCompare(b.category));
  }, [blueprints]);
  const [emailBody, setEmailBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccEmailDraft, setCcEmailDraft] = useState("");
  const [showBcc, setShowBcc] = useState(false);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [bccEmailDraft, setBccEmailDraft] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGeneratedBody, setHasGeneratedBody] = useState(false);
  // One-off instruction typed in the editor's instruction column. It travels
  // with the next Generate and fills {manual_instruction} in the prompt;
  // nothing is stored, so it applies to that call only.
  const [manualInstruction, setManualInstruction] = useState("");
  const [previewFinalPrompt, setPreviewFinalPrompt] = useState("");
  const [previewWebSearchData, setPreviewWebSearchData] = useState("");
  const [previewEmails, setPreviewEmails] = useState("");
  const [previewNotes, setPreviewNotes] = useState("");
  const [previewProfessionalSummary, setPreviewProfessionalSummary] = useState("");
  // Source highlights for the generated body. The editor paints them over the
  // wording it finds, so the body that gets sent stays clean.
  const [previewHighlights, setPreviewHighlights] = useState<EmailHighlight[]>([]);
  // Device-preview width for the body editor ("" = desktop).
  const [previewWidth, setPreviewWidth] = useState("");
  const [openDeviceDropdown, setOpenDeviceDropdown] = useState(false);
  const signatureSelector = "[data-compose-email-signature]";

  const getComposeSignatureBlock = (signature: string) =>
    signature
      ? `<br/><br/><div data-compose-email-signature="true">${signature}</div>`
      : "";

  const removeComposeSignature = (html: string) => {
    if (!html) return "";
    if (typeof document === "undefined") {
      return html;
    }
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    wrapper.querySelectorAll(signatureSelector).forEach((node) => node.remove());
    return wrapper.innerHTML.replace(/(?:<br\s*\/?>|\s)+$/gi, "");
  };

  const appendComposeSignature = (draftHtml: string, signature: string) => {
    const draftWithoutSignature = removeComposeSignature(draftHtml || "");
    return `${draftWithoutSignature}${getComposeSignatureBlock(signature)}`;
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedBlueprintId("");
      setEmailBody("");
      setEmailSubject("");
      setShowCc(false);
      setCcEmails([]);
      setCcEmailDraft("");
      setShowBcc(false);
      setBccEmails([]);
      setBccEmailDraft("");
      setIsGenerating(false);
      setHasGeneratedBody(false);
      setPreviewWidth("");
      setOpenDeviceDropdown(false);
      setPreviewHighlights([]);
      setManualInstruction("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || hasGeneratedBody) return;

    setEmailBody((currentBody) => appendComposeSignature(currentBody, signatureHtml));
  }, [isOpen, signatureHtml, hasGeneratedBody]);

  const getPlainBody = (html: string) => {
    if (!html) return "";
    if (typeof document === "undefined") {
      return html.replace(/<[^>]*>/g, " ");
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const plainBody = getPlainBody(emailBody);
  const getFromOptionId = (fromOption: ContactComposeEmailPopupProps["fromOptions"][number]) =>
    fromOption.id ?? fromOption.outboxId ?? fromOption.OutboxId;
  const getFromOptionLabel = (fromOption: ContactComposeEmailPopupProps["fromOptions"][number]) =>
    fromOption.username || fromOption.emailAddress || fromOption.fromEmail || fromOption.email || "";
  const visibleFromOptions = fromOptions.filter((fromOption) => {
    const optionId = getFromOptionId(fromOption);
    const optionLabel = getFromOptionLabel(fromOption).trim();
    return optionId !== undefined && optionId !== null && optionLabel;
  });

  const handleGenerate = async () => {
    if (!selectedBlueprintId || isGenerating) return;

    setIsGenerating(true);
    try {
      const generatedEmail = await onGenerate(
        Number(selectedBlueprintId),
        manualInstruction.trim(),
      );
      setHasGeneratedBody(true);
      setEmailBody(generatedEmail.emailBody || "");
      setEmailSubject(generatedEmail.emailSubject || "");
      setPreviewFinalPrompt(generatedEmail.finalPrompt || "");
      setPreviewWebSearchData(generatedEmail.webSearchData || "");
      setPreviewEmails(generatedEmail.emails || "");
      setPreviewNotes(generatedEmail.notes || "");
      setPreviewProfessionalSummary(generatedEmail.professionalSummary || "");
      setPreviewHighlights(parseEmailHighlights(generatedEmail.emailHighlights));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async () => {
    if (isSending) return;
    const finalCcEmails = showCc ? mergeRecipients(ccEmails, parseRecipientInput(ccEmailDraft)) : [];
    const finalBccEmails = showBcc ? mergeRecipients(bccEmails, parseRecipientInput(bccEmailDraft)) : [];
    const sent = await onSend({
      emailSubject,
      emailBody,
      ccEmails: finalCcEmails.length ? finalCcEmails : null,
      bccEmails: finalBccEmails.length ? finalBccEmails : null,
    });

    if (sent) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="contact-compose-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(15, 23, 42, 0.36)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        className="contact-compose-modal"
        style={{
          width: "min(1180px, 96vw)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
          padding: 24,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="contact-compose-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 28,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              border: "none",
              background: "transparent",
              color: "#111827",
              fontSize: 18,
              fontWeight: 700,
              cursor: "pointer",
              padding: 0,
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Compose email
          </button>
          <div className="contact-compose-header-actions" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!selectedBlueprintId || isGenerating}
              style={{
                ...lessPriorityButtonStyle,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: (!selectedBlueprintId || isGenerating) ? 0.6 : 1,
                cursor: (!selectedBlueprintId || isGenerating) ? "not-allowed" : "pointer",
              }}
            >
              <FontAwesomeIcon icon={faBolt} />
              {isGenerating ? "Generating..." : "Generate"}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending}
              style={{
                ...defaultButtonStyle,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: isSending ? 0.6 : 1,
                cursor: isSending ? "not-allowed" : "pointer",
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              {isSending ? "Sending..." : "Send"}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close compose"
              title="Close"
              style={{
                width: 38,
                height: 38,
                border: "1px solid #d8dee8",
                borderRadius: 8,
                background: "#fff",
                color: "#64748b",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div
          className="contact-compose-form"
          style={{
            border: "1px solid #e0e6ef",
            borderRadius: 8,
            padding: 24,
            background: "#fff",
          }}
        >
          <div className="contact-compose-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
            <label style={labelStyle}>Select blueprint</label>
            <select
              value={selectedBlueprintId}
              onChange={(event) => setSelectedBlueprintId(event.target.value)}
              style={{ ...inputStyle, width: 390 }}
            >
              <option value="">Select blueprint (optional)...</option>
              {blueprintGroups.map(({ category, blueprints: items }) => (
                <optgroup key={category} label={category}>
                  {items.map((blueprint) => (
                    <option key={blueprint.id} value={blueprint.id}>
                      {blueprint.templateName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            </div>

          <div className="contact-compose-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
            <label style={labelStyle}>From</label>
            <select
              value={selectedFromId}
              onChange={(event) => onFromChange(event.target.value)}
              style={{ ...inputStyle, ...compactFieldStyle }}
            >
              <option value="">Select from email...</option>
              {visibleFromOptions.map((fromOption) => {
                const optionId = getFromOptionId(fromOption);
                return (
                <option key={optionId} value={optionId}>
                  {getFromOptionLabel(fromOption)}
                </option>
                );
              })}
            </select>
            {isSignatureLoading && (
              <span style={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>
                Loading signature...
              </span>
            )}
          </div>

          <div className="contact-compose-field contact-compose-to-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
            <label style={labelStyle}>To</label>
            <input
              value={toEmail}
              readOnly
              style={{
                ...inputStyle,
                ...compactFieldStyle,
                background: "#f8fafc",
                color: "#475569",
                cursor: "not-allowed",
              }}
              placeholder="Contact email not available"
            />
            {!showCc && (
              <button
                type="button"
                onClick={() => setShowCc(true)}
                style={{ border: "none", background: "transparent", color: "#16822f", fontWeight: 700, cursor: "pointer" }}
              >
                Cc
              </button>
            )}
            {!showBcc && (
              <button
                type="button"
                onClick={() => setShowBcc(true)}
                style={{ border: "none", background: "transparent", color: "#16822f", fontWeight: 700, cursor: "pointer" }}
              >
                Bcc
              </button>
            )}
          </div>

          {showCc && (
            <div className="contact-compose-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
              <label style={labelStyle}>Cc</label>
              <RecipientChipInput
                recipients={ccEmails}
                draft={ccEmailDraft}
                onRecipientsChange={setCcEmails}
                onDraftChange={setCcEmailDraft}
                placeholder="Enter Cc email..."
              />
              <button
                type="button"
                onClick={() => {
                  setShowCc(false);
                  setCcEmails([]);
                  setCcEmailDraft("");
                }}
                style={{ border: "none", background: "transparent", color: "#64748b", fontWeight: 700, cursor: "pointer" }}
              >
                X
              </button>
            </div>
          )}

          {showBcc && (
            <div className="contact-compose-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
              <label style={labelStyle}>Bcc</label>
              <RecipientChipInput
                recipients={bccEmails}
                draft={bccEmailDraft}
                onRecipientsChange={setBccEmails}
                onDraftChange={setBccEmailDraft}
                placeholder="Enter Bcc email..."
              />
              <button
                type="button"
                onClick={() => {
                  setShowBcc(false);
                  setBccEmails([]);
                  setBccEmailDraft("");
                }}
                style={{ border: "none", background: "transparent", color: "#64748b", fontWeight: 700, cursor: "pointer" }}
              >
                X
              </button>
            </div>
          )}

          <div className="contact-compose-field" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
            <label style={labelStyle}>Subject</label>
            <input
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Enter subject..."
            />
          </div>

          <div className="contact-compose-editor">
            <div
              style={{
                width: "100%",
                maxWidth:
                  previewWidth === "Mobile"
                    ? 480
                    : previewWidth === "Tab"
                      ? 768
                      : "100%",
                margin: "0 auto",
              }}
            >
              <RichTextEditor
                value={emailBody}
                onChange={setEmailBody}
                height={360}
                showActionButtons={hasGeneratedBody}
                // Not gated on hasGeneratedBody: the instruction is written
                // before the first Generate, not after it.
                showManualInstruction
                manualInstruction={manualInstruction}
                onManualInstructionChange={setManualInstruction}
                onRegenerate={handleGenerate}
                isRegenerating={isGenerating}
                regenerateDisabled={!selectedBlueprintId}
                showDeviceButton
                outputEmailWidth={previewWidth}
                openDeviceDropdown={openDeviceDropdown}
                onDeviceDropdownToggle={() =>
                  setOpenDeviceDropdown((prev) => !prev)
                }
                onDeviceWidthChange={(width) => {
                  setPreviewWidth(width);
                  setOpenDeviceDropdown(false);
                }}
                highlights={previewHighlights}
                finalPrompt={previewFinalPrompt}
                webSearchData={previewWebSearchData}
                insightEmails={previewEmails}
                insightNotes={previewNotes}
                insightProfessionalSummary={previewProfessionalSummary}
              />
            </div>
            <div
              style={{
                minHeight: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 12px 0",
                color: "#64748b",
                fontSize: 12,
              }}
            >
              <span>Characters: {plainBody.length}</span>
              <span>{plainBody.trim() ? plainBody.trim().split(/\s+/).length : 0} words</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactComposeEmailPopup;
